<script setup lang="ts">
import type { Schemas } from "#shopware";

const props = defineProps<{
  product: Schemas["Product"];
}>();

const product = computed(() => props.product);

// const activeAccordion = ref<any | null>(null);

// Track which accordion is currently open
const activeAccordion = ref<number | null>(null);

// Toggle accordion function
const toggleAccordion = (index: number) => {
  activeAccordion.value = activeAccordion.value === index ? null : index;
};

// Update content heights after mount
onMounted(() => {
  // Get all accordion content elements
  const accordionContents = document.querySelectorAll(".accordion-content");

  // Set custom property for each accordion content
  accordionContents.forEach((content) => {
    const contentInner = content.querySelector(".content-inner");
    if (contentInner) {
      const height = contentInner.getBoundingClientRect().height;
      content.style.setProperty("--content-height", `${height}px`);
    }
  });
});

const groupedProperties = computed(() => {
  if (!product.value?.properties) return {};

  const groups: Record<string, { name: string; properties: string[] }> = {};

  product.value.properties.forEach((item) => {
    const groupId = item.groupId;
    const groupName = item.group.name;
    const propertyName = item.name;

    // Initialize group if not exists
    if (!groups[groupId]) {
      groups[groupId] = {
        name: groupName,
        properties: [],
      };
    }

    // Add unique property
    if (!groups[groupId].properties.includes(propertyName)) {
      groups[groupId].properties.push(propertyName);
    }
  });

  return groups;
});
</script>

<template>
  <div class="accordion w-full mx-auto">
    <!-- Accordion Item 1 -->
    <div
      v-if="product.customFields?.short_description"
      class="accordion-item border-y border-custom-grey"
    >
      <button
        class="accordion-header flex justify-between items-center w-full py-4 md:py-6 lg:py-8 text-lg font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent"
        @click="toggleAccordion(0)"
      >
        <span class="text-base font-bold text-chinese-black leading-normal">{{
          $t("productDetails.shortDescription")
        }}</span>
        <svg
          :class="{
            'rotate-180': activeAccordion === 0,
            'rotate-0': activeAccordion !== 0,
          }"
          class="w-2 h-2 transform transition-transform accordion-icon"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
            fill="#141414"
          />
        </svg>
      </button>
      <div
        :style="{
          maxHeight: activeAccordion === 0 ? 'var(--content-height)' : '0',
        }"
        class="accordion-content text-medium-grey bg-white rounded-tr-none rounded-tl-none overflow-hidden"
      >
        <div class="content-inner pt-0 pb-4 md:pb-6 lg:pb-8">
          <p class="text-base text-chinese-black font-normal leading-normal">
            {{ product.customFields?.short_description }}
          </p>
        </div>
      </div>
    </div>

    <!-- Accordion Item 2 -->
    <template v-for="(property, index) in groupedProperties" :key="index">
      <div class="accordion-item accordion-specification border-b border-custom-grey">
        <button
          class="accordion-header flex justify-between items-center w-full py-4 md:py-6 lg:py-8 text-lg font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent"
          @click="toggleAccordion(index)"
        >
          <span class="text-base font-bold text-chinese-black leading-normal">
            {{ property.name }}
          </span>
          <svg
            :class="{
              'rotate-180': activeAccordion === index,
              'rotate-0': activeAccordion !== index,
            }"
            class="w-2 h-2 transform transition-transform accordion-icon"
            viewBox="0 0 8 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
              fill="#141414"
            />
          </svg>
        </button>
        <div
          :style="{
            maxHeight: activeAccordion === index ? 'var(--content-height)' : '0',
          }"
          class="accordion-content text-medium-grey bg-white rounded-tr-none rounded-tl-none overflow-hidden"
        >
          <div class="content-inner pt-0 pb-4 md:pb-6 lg:pb-8">
            <p class="text-base text-chinese-black font-normal leading-normal">
              <template v-for="(value, count) in property.properties" :key="count">
                <span v-if="count !== 0">,</span> {{ value }}
              </template>
            </p>
          </div>
        </div>
      </div>
    </template>

    <!-- Accordion Item 3 -->
    <!-- <div class="accordion-item accordion-suitable border-b border-custom-grey">
      <button
        class="accordion-header flex justify-between items-center w-full py-4 md:py-6 lg:py-8 text-lg font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent"
        @click="toggleAccordion(3)"
      >
        <span class="text-base font-bold text-chinese-black leading-normal">{{
          $t("productDetails.goodToknow")
        }}</span>
        <svg
          :class="{
            'rotate-180': activeAccordion === 3,
            'rotate-0': activeAccordion !== 3,
          }"
          class="w-2 h-2 transform transition-transform accordion-icon"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
            fill="#141414"
          />
        </svg>
      </button>
      <div
        v-show="activeAccordion === 3"
        class="accordion-content text-medium-grey pb-4 md:pb-6 lg:pb-8 bg-white rounded-tr-none rounded-tl-none"
      >
        <p class="text-base text-chinese-black font-normal leading-normal">
          Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam
          nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat,
          sed diam voluptua. At vero eos et accusam et justo duo dolores et ea
          rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem
          ipsum dolor sit amet.
        </p>
      </div>
    </div> -->
  </div>
</template>

<style scoped>
.accordion-icon {
  transition: transform 0.3s ease;
}

.accordion-content {
  transition: max-height 0.3s ease-in-out;
  --content-height: 200px; /* This is a default value, will be updated by JS */
}

.content-inner {
  transition: opacity 0.3s ease;
}
</style>
